const enInsights = [
  {
    id: 3,
    slug: 'the-image-others-have-of-you',
    title: 'The Image Others Have of You',
    date: '2026-10-16',
    dateLabel: '16 OCTOBER 2026',
    locationLine: 'Munich, October 2026 · Michaela Braun',
    body: `Somewhere, someone is carrying around a judgement about you. It is complete, it feels right to them … and it does not reflect who you are.

Perhaps that person was having a bad day. Perhaps part of the story was missing. Perhaps your calm was read as distance and your openness as lightness.

The impulse to correct this is understandable. But it can take up an entire lifetime.

Almost two thousand years ago, long before the "circles of influence", Epictetus drew a boundary that still holds today. On one side lies what is within your power: your judgement, your attitude, your actions. On the other side lies what is not within your power: what forms in other people's minds, because those minds work with their own subjective impressions.

This boundary does not lead to indifference, but to the right measure.

If there is a specific misunderstanding, if it has arisen with someone whose decision matters to you, and if you can resolve it with facts, then address it. Once, with clarity and without a loop of explanations.

If not, you will recognise that it does not fall within your responsibility. This frees up energy for what truly lies within your sphere of influence: the word given, the mistake admitted, the decent treatment of the person sitting across from you right now.

Over time, your actions themselves become reliable evidence.

Some people will still see you differently from who you are. You, too, do not always see others as they are. For me, there is a relieving smile in that realisation.

The image others have of you is a mirror. The only question is whose face appears in it.

This is exactly what my reflection spaces are about: recognising which images you carry, which are attributed to you, and which of them truly deserve your attention.`,
  },
  {
    id: 1,
    slug: 'the-politics-barrier',
    kicker: 'WOMEN. CAREER. POWER.',
    title: 'The Politics Barrier: Why Power Games Hold Women Back, and What Companies Can Do About It',
    date: '2026-06-01',
    dateLabel: '1 JUNE 2026',
    attribution: 'Based on: Salwender, Schoel, Bless & Stahlberg (2023) · University of Mannheim',
    locationLine: 'Munich, June 2026 · Michaela Braun',
    body: `A study by the University of Mannheim shows that in corporate cultures that reward political manoeuvring, women are far more often left on the sidelines and feel out of place. The consequences reach all the way to the executive floor.

Imagine starting a new job. It quickly becomes clear that those who rise do not necessarily do so through ability, but through connections, strategic silence and a nod at the right moment.

Many women do not feel particularly comfortable with this. It is not a question of lacking ambition. It is a question of fit. A research team in Mannheim has shown in a study that this fit is systematically unequally distributed.

The concept of "political games", known in the research literature as "organizational politics", refers to behaviour aimed at building power and influence, often outside formal structures. Forming alliances. Withholding information strategically. Choosing the right mentor.

The study suggests that corporate structures can strongly influence career paths, regardless of individual performance and competence.

Summarised from Salwender et al., Social Psychological and Personality Science, 2023

### What the results show

Under "power games" conditions, women are considerably more afraid of negative reactions from colleagues and male superiors. Under these conditions, women also doubt their own competence and abilities significantly more often. The result is what is known as a "lack of fit" experience.

So the point is not that women fundamentally have fewer leadership ambitions. The point is under which conditions they develop these ambitions, or do not.

### The deeper mechanism

Behind the term "lack of fit" lies a now well-established psychological theory: people experience stress and self-doubt when the demands of a role do not match the image they have of themselves. When a corporate culture rewards traits that society tends to attribute to men, and that men also display more often, many women receive a silent message. The traits in question are assertiveness at the expense of others, strategic manoeuvring and visible displays of power. The message is: "You don't belong here."

Those who believe they lack the necessary abilities are less likely to pursue leadership positions, regardless of whether that doubt is justified.

### What this means for companies

If companies want more women to pursue leadership positions, it is not the women who need to change. Companies need to change their culture.

As long as informal power games determine careers, every diversity initiative can only scratch the surface. Mentoring programmes, quotas, unconscious bias training: all of them fall short if the underlying structure of advancement remains intact.

Three things can help. First, transparent promotion criteria linked to actual performance. Second, a feedback culture that makes informal influence visible and therefore open to challenge. Third, leadership that neither rewards nor silently tolerates political manoeuvring.

### What remains?

The question that remains is not an academic one. Every executive, every HR department and ultimately every employee must answer it for themselves: What am I really rewarding here?`,
  },
];

export default enInsights;
